import React, { useState, useRef } from 'react';
import * as mammoth from 'mammoth';

const SimpleWorkingParser = () => {
  const [parsedData, setParsedData] = useState(null);
  const [status, setStatus] = useState({ message: '', type: '' });
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const processFile = async (file) => {
    if (!file.name.match(/\.(docx|doc)$/)) {
      setStatus({ message: 'გთხოვთ აირჩიოთ Word ფაილი (.doc ან .docx)', type: 'error' });
      return;
    }

    setFileName(file.name.replace(/\.[^/.]+$/, ''));
    setStatus({ message: 'ფაილი იტვირთება...', type: 'info' });

    try {
      const arrayBuffer = await file.arrayBuffer();
      
      // Extract both HTML and text
      const htmlResult = await mammoth.convertToHtml({ 
        arrayBuffer,
        convertImage: mammoth.images.imgElement(function(image) {
          return image.read("base64").then(function(imageBuffer) {
            return {
              src: "data:" + image.contentType + ";base64," + imageBuffer
            };
          });
        })
      });
      
      const textResult = await mammoth.extractRawText({ arrayBuffer });
      
      // Parse the content
      const result = parseContent(textResult.value, htmlResult.value);
      setParsedData(result);
      
      setStatus({ 
        message: `✅ ნაპოვნია ${result.packets[0].questions_data.length} კითხვა`, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error:', error);
      setStatus({ 
        message: '❌ შეცდომა: ' + error.message, 
        type: 'error' 
      });
    }
  };

  const parseContent = (text, html) => {
    // Extract hyperlinks
    const linkMap = extractHyperlinks(html);
    
    // Extract images
    const imageMap = extractImages(html);
    
    // Parse metadata
    const { title, date, authors } = parseMetadata(text);
    
    // Find all questions
    const questions = parseQuestions(text, linkMap, imageMap);
    
    return {
      packets: [{
        id: 1,
        title: title || 'უსათაურო პაკეტი',
        difficulty: 'Intermediate',
        questions: questions.length,
        author: authors.length > 0 ? authors : ['უცნობი ავტორი'],
        date: date || new Date().toLocaleDateString('ka-GE'),
        questions_data: questions
      }]
    };
  };

  const parseMetadata = (text) => {
    const lines = text.split('\n').slice(0, 20);
    let title = '';
    let date = '';
    const authors = [];
    
    for (const line of lines) {
      if (line.includes('საავტორო ტურნირი') || line.includes('ტურნირი') || line.includes('HESOYAM')) {
        title = line.trim();
      }
      
      const dateMatch = line.match(/\d{1,2}\.\d{1,2}\.\d{4}/);
      if (dateMatch) {
        date = dateMatch[0];
      }
      
      const authorMatches = line.match(/([ა-ჰ\s]+)\s*\([\d.]+\)/g);
      if (authorMatches) {
        authors.push(...authorMatches);
      }
    }
    
    return { title, date, authors };
  };

  const parseQuestions = (text, linkMap, imageMap) => {
    const questions = [];
    
    // Split text into potential questions using regex
    // Match lines that start with number followed by period
    const questionPattern = /^(\d{1,3})\.\s+/gm;
    const matches = Array.from(text.matchAll(questionPattern));
    
    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      const questionNumber = parseInt(match[1]);
      
      // Get text from this question to the next (or end)
      const startIdx = match.index;
      const endIdx = i < matches.length - 1 ? matches[i + 1].index : text.length;
      const questionBlock = text.substring(startIdx, endIdx);
      
      // Parse this question block
      const question = parseQuestionBlock(questionBlock, questionNumber, linkMap, imageMap);
      questions.push(question);
    }
    
    return questions;
  };

  const parseQuestionBlock = (block, questionNumber, linkMap, imageMap) => {
    // Remove the question number from the beginning
    const blockWithoutNumber = block.replace(/^\d+\.\s*/, '');
    
    // Initialize result
    const result = {
      id: questionNumber,
      text: '',
      answer: '',
      author: '',
      comment: '',
      source: '',
      url: '',
      image: imageMap[questionNumber] || null
    };
    
    // Define field patterns - MUST handle fields that run together
    // Key insight: fields can appear on same line, so we need to split them
    
    // First, find where each field starts
    const fieldPositions = [];
    
    // Find პასუხი
    const answerMatch = blockWithoutNumber.match(/პასუხი\s*:\s*/i);
    if (answerMatch) {
      fieldPositions.push({ 
        field: 'answer', 
        start: answerMatch.index,
        marker: answerMatch[0]
      });
    }
    
    // Find კომენტარი
    const commentMatch = blockWithoutNumber.match(/კომენტარი\s*:\s*/i);
    if (commentMatch) {
      fieldPositions.push({ 
        field: 'comment', 
        start: commentMatch.index,
        marker: commentMatch[0]
      });
    }
    
    // Find წყარო
    const sourceMatch = blockWithoutNumber.match(/წყარო\s*:\s*/i);
    if (sourceMatch) {
      fieldPositions.push({ 
        field: 'source', 
        start: sourceMatch.index,
        marker: sourceMatch[0]
      });
    }
    
    // Find ავტორი
    const authorMatch = blockWithoutNumber.match(/ავტორი\s*:\s*/i);
    if (authorMatch) {
      fieldPositions.push({ 
        field: 'author', 
        start: authorMatch.index,
        marker: authorMatch[0]
      });
    }
    
    // Sort by position
    fieldPositions.sort((a, b) => a.start - b.start);
    
    // Extract question text (everything before first field)
    if (fieldPositions.length > 0) {
      result.text = blockWithoutNumber.substring(0, fieldPositions[0].start).trim();
    } else {
      result.text = blockWithoutNumber.trim();
      return result;
    }
    
    // Extract each field's content
    for (let i = 0; i < fieldPositions.length; i++) {
      const current = fieldPositions[i];
      const contentStart = current.start + current.marker.length;
      const contentEnd = i < fieldPositions.length - 1 
        ? fieldPositions[i + 1].start 
        : blockWithoutNumber.length;
      
      const content = blockWithoutNumber.substring(contentStart, contentEnd).trim();
      
      // Assign to appropriate field
      result[current.field] = content;
    }
    
    // Post-process source field for URLs
    if (result.source) {
      const urls = [];
      
      // Count "ბმული" occurrences
      const linkCount = (result.source.match(/ბმული/g) || []).length;
      
      // Try to find actual URLs from linkMap
      if (linkCount > 0 && linkMap.length > 0) {
        // Get URLs from linkMap
        linkMap.forEach(link => {
          if (link.text === 'ბმული' || result.source.includes(link.text)) {
            urls.push(link.url);
          }
        });
      }
      
      if (urls.length > 0) {
        result.url = urls.join(', ');
        result.source = result.source.replace(/ბმული/g, '[ბმული]');
      } else if (linkCount > 0) {
        result.url = `[${linkCount} ბმული]`;
      }
    }
    
    // Clean up - sometimes author might be at the end of source
    if (!result.author && result.source) {
      const sourceAuthorMatch = result.source.match(/ავტორი\s*:\s*(.+)$/i);
      if (sourceAuthorMatch) {
        result.author = sourceAuthorMatch[1].trim();
        result.source = result.source.replace(sourceAuthorMatch[0], '').trim();
      }
    }
    
    return result;
  };

  const extractHyperlinks = (html) => {
    const links = [];
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    const anchors = tempDiv.querySelectorAll('a[href]');
    anchors.forEach(anchor => {
      links.push({
        url: anchor.href,
        text: anchor.textContent.trim()
      });
    });
    
    return links;
  };

  const extractImages = (html) => {
    const imageMap = {};
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    const images = tempDiv.querySelectorAll('img[src]');
    images.forEach((img, index) => {
      if (img.src && img.src.startsWith('data:')) {
        imageMap[index + 1] = img.src;
      }
    });
    
    return imageMap;
  };

  const copyToClipboard = () => {
    const jsonText = JSON.stringify(parsedData, null, 2);
    navigator.clipboard.writeText(jsonText).then(() => {
      setStatus({ message: '✅ დაკოპირდა!', type: 'success' });
    });
  };

  const downloadJSON = () => {
    if (!parsedData) return;
    
    const jsonText = JSON.stringify(parsedData, null, 2);
    const blob = new Blob([jsonText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName || 'quiz'}_parsed.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setParsedData(null);
    setFileName('');
    setStatus({ message: '', type: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      <h1>Word → JSON Parser</h1>
      
      <div 
        onDrop={handleDrop} 
        onDragOver={handleDragOver}
        style={{
          border: '2px dashed #ccc',
          padding: '40px',
          textAlign: 'center',
          marginTop: '20px'
        }}
      >
        <h3>აირჩიეთ Word ფაილი</h3>
        <input
          ref={fileInputRef}
          type="file"
          accept=".docx,.doc"
          onChange={handleFileSelect}
        />
      </div>
      
      {status.message && (
        <div style={{
          padding: '10px',
          marginTop: '20px',
          borderRadius: '5px',
          backgroundColor: status.type === 'error' ? '#ffcccc' : '#ccffcc',
          color: status.type === 'error' ? '#cc0000' : '#006600'
        }}>
          {status.message}
        </div>
      )}
      
      {parsedData && (
        <div style={{ marginTop: '20px' }}>
          <h3>შედეგი:</h3>
          <pre style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '10px', 
            overflow: 'auto',
            maxHeight: '400px'
          }}>
            {JSON.stringify(parsedData, null, 2)}
          </pre>
          
          <div style={{ marginTop: '20px' }}>
            <button onClick={copyToClipboard}>📋 კოპირება</button>
            <button onClick={downloadJSON} style={{ marginLeft: '10px' }}>💾 ჩამოტვირთვა</button>
            <button onClick={reset} style={{ marginLeft: '10px' }}>🔄 ახალი</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleWorkingParser;